import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DistributionChart from "./components/DistributionChart";
import FilterBar from "./components/FilterBar";
import MetricCards from "./components/MetricCards";
import PerformanceTable from "./components/PerformanceTable";
import TopUsersTable from "./components/TopUsersTable";
import TrendChart from "./components/TrendChart";
import UserActivityChart from "./components/UserActivityChart";
import { FaChartLine, FaCreditCard, FaPercentage, FaTicketAlt, FaUsers, FaWallet } from "react-icons/fa";
import reportService from "../../services/reportService";
import moment from "moment-timezone";


const toNumber = (value) => Number(value) || 0;

const formatDateLabel = (value) => {
  const date = new Date(value);

  if (!Number.isFinite(date.valueOf())) return "";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
};

const toDateInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getDateRangeByTab = (tab) => {
  const today = new Date();
  const startDate = new Date(today);
  const endDate = new Date(today);

  if (tab === "yesterday") {
    startDate.setDate(today.getDate() - 1);
    endDate.setDate(today.getDate() - 1);
  }

  if (tab === "last7") {
    startDate.setDate(today.getDate() - 6);
  }

  if (tab === "last30") {
    startDate.setDate(today.getDate() - 29);
  }

  return {
    startDate: toDateInputValue(startDate),
    endDate: toDateInputValue(endDate),
  };
};

const formatMoney = (value) => {
  const amount = Math.abs(toNumber(value));
  const sign = value < 0 ? "-" : value > 0 ? "+" : "";

  if (amount >= 1000000) {
    return `${sign}${(amount / 1000000).toFixed(2)}M`;
  }

  return `${sign}${Math.round(amount).toLocaleString("en-US")}`;
};

const formatMetricMoney = (value) => {
  const amount = toNumber(value);

  if (Math.abs(amount) >= 1000000) {
    return `${(amount / 1000000).toFixed(2)}M`;
  }

  return `${Math.round(amount).toLocaleString("en-US")}`;
};

const formatMixAmount = (value) => {
  const amount = toNumber(value);

  if (Math.abs(amount) >= 1000000) {
    return `${(amount / 1000000).toFixed(2)}M`;
  }

  return `${(amount / 1000).toFixed(1)}K`;
};

const formatPercent = (value) => `${toNumber(value).toFixed(1)}%`;

const calculateMargin = (netProfit, totalBet) => {
  const betAmount = toNumber(totalBet);

  if (betAmount === 0) return 0;

  return (toNumber(netProfit) / betAmount) * 100;
};

const parseMixDetails = (value) => {
  if (!value) return [];

  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Failed to parse dashboard mix details", error);
    return [];
  }
};

const emptyAggregate = {
  totalBet: 0,
  totalSingleBet: 0,
  totalMixBet: 0,
  totalWin: 0,
  totalSingleWin: 0,
  totalMixWin: 0,
  totalBettedUser: 0,
  mixDetails: [],
};

const getRowsFromResponse = (response) => {
  const data = response?.data;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.payload)) return data.payload;
  if (Array.isArray(data?.dashboardData)) return data.dashboardData;
  if (Array.isArray(data?.bettingReports)) return data.bettingReports;
  if (Array.isArray(data?.result)) return data.result;

  return [];
};

const emptyUserDashboard = {
  bettedUserCount: 0,
  topWinUsers: [],
};

const getUserDashboardFromResponse = (response) => {
  const data = response?.data;

  if (data?.payload && !Array.isArray(data.payload)) return data.payload;
  if (data?.dashboardData && !Array.isArray(data.dashboardData)) return data.dashboardData;
  if (data?.result && !Array.isArray(data.result)) return data.result;
  if (data && !Array.isArray(data)) return data;

  return emptyUserDashboard;
};

const aggregateDashboardRows = (items = []) => {
  const mixByTeamCount = new Map();

  const aggregate = items.reduce(
    (total, item) => {
      total.totalBet += toNumber(item.totalBet);
      total.totalSingleBet += toNumber(item.totalSingleBet);
      total.totalMixBet += toNumber(item.totalMixBet);
      total.totalWin += toNumber(item.totalWin);
      total.totalSingleWin += toNumber(item.totalSingleWin);
      total.totalMixWin += toNumber(item.totalMixWin);
      total.totalBettedUser += toNumber(item.totalBettedUser);

      parseMixDetails(item.mixDetails).forEach((mixItem) => {
        const teamCount = toNumber(mixItem.teamCount);
        const existing = mixByTeamCount.get(teamCount) || {
          teamCount,
          totalBetCount: 0,
          totalBetAmount: 0,
          totalTurnOver: 0,
        };

        existing.totalBetCount += toNumber(mixItem.totalBetCount);
        existing.totalBetAmount += toNumber(mixItem.totalBetAmount);
        existing.totalTurnOver += toNumber(mixItem.totalTurnOver);
        mixByTeamCount.set(teamCount, existing);
      });

      return total;
    },
    { ...emptyAggregate }
  );

  return {
    ...aggregate,
    mixDetails: Array.from(mixByTeamCount.values()).sort((first, second) => first.teamCount - second.teamCount),
  };
};

const buildMetricCards = (aggregate = emptyAggregate, userDashboard = emptyUserDashboard, dashboardRows = [], dateRange = {}) => {
  //console.log("Aggregate Data:", aggregate); // Debugging line to check the contents of aggregate
  //console.log("User Dashboard Data:", userDashboard); // Debugging line to check the contents of userDashboard
  //console.log("Dashboard Rows Data:", dashboardRows); // Debugging line to check the contents of dashboardRows
  //console.log("Date Range Data:", dateRange, moment().format("YYYY-MM-DD")); // Debugging line to check the contents of dateRange
  const totalBet = toNumber(aggregate.totalBet);
  const totalWin = toNumber(aggregate.totalWin);
  const netProfit = totalBet - totalWin;
  const profitMargin = calculateMargin(netProfit, totalBet);
  const activeBettors = toNumber(dateRange.startDate === moment().format("YYYY-MM-DD") || dateRange.endDate === moment().format("YYYY-MM-DD")
  ? dashboardRows[0]?.totalBettedUser : userDashboard.bettedUserCount);
  const averageBet = activeBettors > 0 ? totalBet / activeBettors : 0;

  return [
    {
      label: "Total Bet Amount",
      value: formatMetricMoney(totalBet),
      change: "0.0%",
      detail: "",
      tone: "blue",
      icon: FaWallet,
      neutral: true,
    },
    {
      label: "Total Payout",
      value: formatMetricMoney(totalWin),
      change: "0.0%",
      detail: "",
      tone: "blue",
      icon: FaCreditCard,
      neutral: true,
    },
    {
      label: "Net Profit",
      value: formatMoney(netProfit),
      change: "0.0%",
      detail: "",
      tone: netProfit >= 0 ? "green" : "orange",
      icon: FaChartLine,
      neutral: true,
    },
    {
      label: "Profit Margin",
      value: formatPercent(profitMargin),
      change: "0.0%",
      detail: "",
      tone: profitMargin >= 0 ? "green" : "orange",
      icon: FaPercentage,
      neutral: true,
    },
    {
      label: "Active Bettors",
      value: activeBettors.toLocaleString("en-US"),
      change: "0.0%",
      detail: "",
      tone: "sky",
      icon: FaUsers,
      neutral: true,
    },
    {
      label: "Average Bet",
      value: formatMetricMoney(averageBet),
      change: "0.0%",
      detail: "",
      tone: "orange",
      icon: FaTicketAlt,
      neutral: true,
    },
  ];
};

const buildTrendData = (items = []) => {
  const rowsByDate = new Map();

  items.forEach((item) => {
    const dateKey = item.selectedDate ? item.selectedDate.slice(0, 10) : "";
    const existing = rowsByDate.get(dateKey) || { selectedDate: item.selectedDate, totalBet: 0, totalWin: 0 };

    existing.totalBet += toNumber(item.totalBet);
    existing.totalWin += toNumber(item.totalWin);
    rowsByDate.set(dateKey, existing);
  });

  return Array.from(rowsByDate.values())
    .sort((first, second) => new Date(first.selectedDate) - new Date(second.selectedDate))
    .map((item) => ({
      date: formatDateLabel(item.selectedDate),
      selectedDate: item.selectedDate,
      amount: Number((item.totalBet / 1000000).toFixed(2)),
      profit: Number(((item.totalBet - item.totalWin) / 1000000).toFixed(2)),
    }));
};

const buildUserActivityData = (items = []) => {
  const rowsByDate = new Map();

  items.forEach((item) => {
    const dateKey = item.selectedDate ? item.selectedDate.slice(0, 10) : "";
    const existing = rowsByDate.get(dateKey) || {
      selectedDate: item.selectedDate,
      active: 0,
      betting: 0,
    };

    existing.active += toNumber(item.totalBettedUser);
    existing.betting += toNumber(item.totalBettedUser);
    rowsByDate.set(dateKey, existing);
  });

  return Array.from(rowsByDate.values())
    .sort((first, second) => new Date(first.selectedDate) - new Date(second.selectedDate))
    .map((item) => ({
      date: formatDateLabel(item.selectedDate),
      active: item.active,
      betting: item.betting,
    }));
};

const userAvatarColors = [
  "bg-customAccentBlue",
  "bg-customAccent",
  "bg-customFontGreen",
  "bg-customOrange",
  "bg-customTurquosie",
];

const getUserInitials = (user = {}) => {
  const displayName = String(user.name || user.username || "").trim();

  if (!displayName) return "U";

  return displayName
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

const buildTopUsers = (userDashboard = emptyUserDashboard) => {
  const topWinUsers = Array.isArray(userDashboard.topWinUsers) ? userDashboard.topWinUsers : [];

  return topWinUsers.map((user, index) => ({
    rank: index + 1,
    initials: getUserInitials(user),
    user: user.username || "Unknown User",
    name: user.name || "Unknown Name",
    amount: formatMetricMoney(user.betAmount),
    net: formatMoney(toNumber(user.winAmount) - toNumber(user.betAmount)),
    color: userAvatarColors[index % userAvatarColors.length],
  }));
};

const buildMixDistribution = (aggregate = emptyAggregate) => {
  const mixDetails = Array.isArray(aggregate.mixDetails) ? aggregate.mixDetails : [];
  //console.log("Mix Details:", mixDetails); // Debugging line to check the contents of mixDetails
  const totalMixBet = mixDetails.filter(item => item.teamCount > 1)
    .reduce((total, item) => total + toNumber(item.totalBetAmount), 0) || toNumber(aggregate.totalMixBet);

  return mixDetails.filter(item => item.teamCount > 1).map((item) => {
    const amount = toNumber(item.totalBetAmount);

    return {
      label: `${item.teamCount} Mix`,
      value: amount,
      displayValue: formatMixAmount(amount),
      percent: totalMixBet > 0 ? Math.round((amount / totalMixBet) * 100) : 0,
      win: formatMixAmount(toNumber(item.totalTurnOver)),
    };
  });
};

const buildBetTypePerformance = (aggregate = emptyAggregate) => {
  const singleBet = toNumber(aggregate.totalSingleBet);
  const singleWin = toNumber(aggregate.totalSingleWin);
  const mixBet = toNumber(aggregate.totalMixBet);
  const mixWin = toNumber(aggregate.totalMixWin);
  const totalBet = toNumber(aggregate.totalBet);
  const totalWin = toNumber(aggregate.totalWin);

  const buildRow = (type, bet, win, total = false) => {
    const netResult = bet - win;

    return {
      type,
      amount: formatMetricMoney(bet),
      payout: formatMetricMoney(win),
      result: formatMoney(netResult),
      margin: formatPercent(calculateMargin(netResult, bet)),
      total,
      negative: netResult < 0,
    };
  };

  return [
    buildRow("Single", singleBet, singleWin),
    buildRow("Mix", mixBet, mixWin),
    buildRow("Total", totalBet, totalWin, true),
  ];
};

const DashboardV2 = () => {
  const hasFetchedInitialData = useRef(false);
  const [activeTab, setActiveTab] = useState("today");
  const [dateRange, setDateRange] = useState(() => getDateRangeByTab("today"));
  const [dashboardRows, setDashboardRows] = useState([]);
  const [userDashboard, setUserDashboard] = useState(emptyUserDashboard);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");

  const dashboardData = useMemo(() => {
    const aggregate = aggregateDashboardRows(dashboardRows);

    return {
      metricCards: buildMetricCards(aggregate, userDashboard, dashboardRows, dateRange),
      trendData: buildTrendData(dashboardRows),
      userActivityData: buildUserActivityData(dashboardRows),
      userConversion: dashboardRows.length > 0 ? 100 : 0,
      mixDistribution: buildMixDistribution(aggregate),
      betTypePerformance: buildBetTypePerformance(aggregate),
      topUsers: buildTopUsers(userDashboard),
    };
  }, [dashboardRows, userDashboard]);

  const fetchBettingAnalysisData = useCallback(async (range = dateRange) => {
    setIsLoading(true);

    try {
      const userDetails = JSON.parse(sessionStorage.getItem("userDetails") || "{}");
      const requestPayload = {
        startDate: range.startDate,
        endDate: range.endDate,
        companyID: userDetails?.companyId || "909",
      };
      const [bettingResponse, userResponse] = await Promise.all([
        reportService.dashboardBetting(requestPayload),
        reportService.dashboardUser(requestPayload),
      ]);

      setDashboardRows(getRowsFromResponse(bettingResponse));
      setUserDashboard(getUserDashboardFromResponse(userResponse));
      setLastUpdated(
        new Intl.DateTimeFormat("en-US", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date())
      );
    } catch (error) {
      console.error("Failed to fetch dashboard betting data", error);
      setDashboardRows([]);
      setUserDashboard(emptyUserDashboard);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab !== "custom") {
      const nextDateRange = getDateRangeByTab(tab);
      setDateRange(nextDateRange);
      fetchBettingAnalysisData(nextDateRange);
    }
  };

  const handleDateChange = (key, value) => {
    setActiveTab("custom");
    setDateRange((current) => ({
      ...current,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (hasFetchedInitialData.current) return;

    hasFetchedInitialData.current = true;
    fetchBettingAnalysisData(dateRange);
  }, [dateRange, fetchBettingAnalysisData]);

  return (
    <div className="min-h-full bg-customLightMilkWhite px-3 py-3 font-inter text-[13px] text-customPrimaryText dark:bg-custom_dark_background dark:text-customHeading sm:px-4 lg:px-5">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-3">
        <div>
          <h1 className="text-xl font-bold leading-tight text-customInk dark:text-customHeading">Dashboard Overview</h1>
          <p className="mt-1 text-xs text-customSecondaryText dark:text-customMuted">
            Betting operations snapshot for today.
          </p>
        </div>
        <FilterBar
          activeTab={activeTab}
          dateRange={dateRange}
          isLoading={isLoading}
          lastUpdated={lastUpdated}
          onDateChange={handleDateChange}
          onRefresh={() => fetchBettingAnalysisData(dateRange)}
          onTabChange={handleTabChange}
        />
        <div className="relative">
          {isLoading ? (
            <div className="absolute inset-0 z-10 grid min-h-[240px] place-items-center rounded-xl bg-white/70 backdrop-blur-sm dark:bg-custom_dark_background/70">
              <div className="rounded-lg border border-customAccentBlue/20 bg-white px-4 py-3 text-xs font-semibold text-customAccentBlue shadow-sm dark:border-customBorder dark:bg-customSurface dark:text-customHeading">
                Loading dashboard data...
              </div>
            </div>
          ) : null}
          <div className={isLoading ? "pointer-events-none opacity-60" : ""}>
            <MetricCards metrics={dashboardData.metricCards} />
            <div className="mt-3 grid gap-3 xl:grid-cols-[1.1fr_1fr]">
              <TrendChart data={dashboardData.trendData} />
              <UserActivityChart data={dashboardData.userActivityData} conversion={dashboardData.userConversion} />
            </div>
            <div className="mt-3 grid gap-3 xl:grid-cols-[1fr_0.95fr_0.95fr]">
              <DistributionChart data={dashboardData.mixDistribution} />
              <PerformanceTable rows={dashboardData.betTypePerformance} />
              <TopUsersTable users={dashboardData.topUsers} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardV2;
